import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { triggerSparkSync } from 'src/services/client';

interface SparkSyncTriggerState {
  isSyncing: boolean;
  error: string | null;
}

interface UseSparkSyncTriggerReturn extends SparkSyncTriggerState {
  handleTriggerSync: () => Promise<void>;
}

const toastConfig = {
  autoClose: 5000,
  position: 'top-center' as const,
};

const useSparkSyncTrigger = (): UseSparkSyncTriggerReturn => {
  const [state, setState] = useState<SparkSyncTriggerState>({
    isSyncing: false,
    error: null,
  });

  const handleTriggerSync = useCallback(async () => {
    if (state.isSyncing) return;

    setState((prev: SparkSyncTriggerState) => ({
      ...prev,
      isSyncing: true,
      error: null,
    }));

    try {
      const response: any = await triggerSparkSync();

      if (response.ok || response.status === 200 || response.status === 202) {
        const data = await response.json();

        const message = data.message
          ? `Spark sync triggered successfully. ${data.message} for all projects`
          : 'Spark sync triggered successfully.';
        toast.success(message, toastConfig);
      } else {
        toast.error(
          'Failed to trigger Spark sync. Please try again.',
          toastConfig
        );
        setState((prev: SparkSyncTriggerState) => ({
          ...prev,
          error: 'Failed to trigger Spark sync',
        }));
      }
    } catch (error) {
      console.error('Error triggering Spark sync:', error);
      toast.error(
        'Failed to trigger Spark sync. Please try again.',
        toastConfig
      );
      setState((prev: SparkSyncTriggerState) => ({
        ...prev,
        error: 'Error triggering Spark sync',
      }));
    } finally {
      setState((prev: SparkSyncTriggerState) => ({
        ...prev,
        isSyncing: false,
      }));
    }
  }, [state.isSyncing]);

  return {
    ...state,
    handleTriggerSync,
  };
};

export default useSparkSyncTrigger;